import {
    DomainDefaultsDataSourceQueryResult,
    useDomainDefaultsDataSourceQuery,
    useDomainDefaultsQuery,
} from "@/components/domain_settings/defaults/queries.generated";
import {ProvisionedDevice} from "../../types";
import {DefaultDeviceVariant, DefaultVendor} from "@/api";
import {useDomainNameQuery} from "@/hooks";
import {ContextFilters, useAuth} from "@/context/AuthProvider";

type DomainAdminData = {
    vendors: DefaultVendor[];
    variants: DefaultDeviceVariant[];

    domainDefaults: {
        vendor: DefaultVendor;
        deviceVariant: DefaultDeviceVariant;
    };
};

export type DataSource = {
    state: "idle" | "loading" | "ok";

    userId?: number;
    device?: ProvisionedDevice;

    domainAdminData?: DomainAdminData;
};

type DomainDefaultsDataSourceQueryItem =
    | DomainDefaultsDataSourceQueryResult["data"]["vendors"][number]
    | DomainDefaultsDataSourceQueryResult["data"]["deviceVariants"]["items"][number];

const mapToDefaultType = (item: DomainDefaultsDataSourceQueryItem): DefaultVendor | DefaultDeviceVariant => ({
    id: item.id,
    name: item.name,
});

function useDomainAdminQueries(domainId: number, isOpen: boolean): DomainAdminData & {loading: boolean} {
    const auth = useAuth();
    const {domainName} = useDomainNameQuery(domainId);

    const canViewDomainDefaults = auth.hasPermission("view_domain", ContextFilters.domain(domainName));

    const domainDefaultsQuery = useDomainDefaultsQuery({
        variables: {
            domainId,
        },
        fetchPolicy: "no-cache",
        skip: !isOpen || !canViewDomainDefaults,
    });

    const vendorsAndVariantsQuery = useDomainDefaultsDataSourceQuery({
        variables: {
            domainId,
        },
        fetchPolicy: "no-cache",
        skip: !isOpen || !canViewDomainDefaults,
    });

    const domainDefaultsLoading = !canViewDomainDefaults
        ? false
        : vendorsAndVariantsQuery.loading || domainDefaultsQuery.loading;

    if (!isOpen || domainDefaultsLoading || !canViewDomainDefaults) {
        return {
            vendors: [],
            variants: [],
            domainDefaults: undefined,
            loading: isOpen && domainDefaultsLoading,
        };
    }

    return {
        vendors: (vendorsAndVariantsQuery.data?.vendors.map(mapToDefaultType) as DefaultVendor[]) || [],
        variants:
            (vendorsAndVariantsQuery.data?.deviceVariants.items.map(mapToDefaultType) as DefaultDeviceVariant[]) || [],
        domainDefaults: {
            vendor: domainDefaultsQuery.data.domainDefaults.vendor,
            deviceVariant: domainDefaultsQuery.data.domainDefaults.deviceVariant,
        },
        loading: false,
    };
}

// TODO: we since this data source would just return everything it gets in case of a simple
// user (no admin data), we should not even use this data source at all
export function useDataSource(
    domainId: number,
    isOpen: boolean,
    extra: {
        device?: ProvisionedDevice;
        userId?: number;
    },
): DataSource {
    const domainAdminData = useDomainAdminQueries(domainId, isOpen);

    if (!isOpen || domainAdminData.loading)
        return {
            state: isOpen ? "loading" : "idle",
            userId: extra.userId,
            device: extra.device,
            domainAdminData: undefined,
        };

    return {
        state: "ok",
        userId: extra.userId,
        device: extra.device,
        domainAdminData: {
            domainDefaults: domainAdminData.domainDefaults,
            vendors: domainAdminData.vendors,
            variants: domainAdminData.variants,
        },
    };
}
